import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import type { ZodType } from "zod";
import { useOptionalAuth } from "@/context/AuthContext";

export const usePreferenceScope = (): string => {
    const auth = useOptionalAuth() as { user: { id: number } | null } | null;
    let company = "none";
    try {
        company = localStorage.getItem("vigepra_active_company") || "none";
    } catch {
        /* Storage is optional. */
    }
    return `${auth?.user?.id || "anonymous"}:${company}`;
};

export const useStoredPreference = <T>(
    key: string | null,
    schema: ZodType<T>,
    initial: T,
    session = false
): [T, Dispatch<SetStateAction<T>>] => {
    const scope = usePreferenceScope();
    const scopedKey = key ? `${key}:${scope}` : null;
    const read = (): T => {
        try {
            const storage = session ? sessionStorage : localStorage;
            const parsed = schema.safeParse(
                JSON.parse(
                    scopedKey ? storage.getItem(scopedKey) || "null" : "null"
                )
            );
            return parsed.success ? parsed.data : initial;
        } catch {
            return initial;
        }
    };
    const [state, setState] = useState(() => ({
        key: scopedKey,
        value: read(),
    }));
    const value = state.key === scopedKey ? state.value : read();
    const setValue: Dispatch<SetStateAction<T>> = (update) =>
        setState((previous) => {
            const current =
                previous.key === scopedKey ? previous.value : read();
            return {
                key: scopedKey,
                value:
                    typeof update === "function"
                        ? (update as (current: T) => T)(current)
                        : update,
            };
        });
    useEffect(() => {
        if (state.key !== scopedKey) {
            setState({ key: scopedKey, value: read() });
        }
    }, [scopedKey]);
    useEffect(() => {
        if (!scopedKey || state.key !== scopedKey) {
            return;
        }
        try {
            (session ? sessionStorage : localStorage).setItem(
                scopedKey,
                JSON.stringify(value)
            );
        } catch {
            /* Preferences must not block work. */
        }
    }, [scopedKey, state, value, session]);
    return [value, setValue];
};
